import { createHash } from 'node:crypto';
import { existsSync } from 'node:fs';
import { mkdir, readFile, readdir, writeFile } from 'node:fs/promises';
import { basename, dirname, join, relative } from 'node:path';

import { execa } from 'execa';
import fg from 'fast-glob';

import { PATHS } from '@/core/constants/paths.js';
import { toPosixPath } from '@/core/path-utils.js';
import type { PentestWorkflowName } from '@/core/types/pentest.js';

export interface ProjectScriptResult {
  script: string;
  status: 'completed' | 'blocked' | 'failed';
  artifact_paths: string[];
  exit_code: number | null;
  summary: string;
}

export interface ModuleDocEvidence {
  module: string;
  paths: string[];
  content: string;
}

export interface TestEvidence {
  path: string;
  content: string;
}

export interface SuspiciousRoute {
  path: string;
  status: number;
  kind: string;
}

export function toLocalTimestamp(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}-${hours}-${minutes}-${seconds}`;
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export async function writeJson(target: string, data: unknown): Promise<void> {
  await mkdir(dirname(target), { recursive: true });
  await writeFile(target, `${JSON.stringify(data, null, 2)}\n`);
}

export async function readJsonIfExists<T>(target: string): Promise<T | null> {
  if (!existsSync(target)) {
    return null;
  }

  return JSON.parse(await readFile(target, 'utf8')) as T;
}

export async function hashRelevantInputs(
  projectRoot: string,
  workflow: PentestWorkflowName,
  extra: Record<string, string | string[] | null | undefined> = {},
): Promise<string> {
  const files = (
    await fg(
      [
        'docs/modules/**/*.md',
        'tests/**/*',
        'src/**/*',
        'app/**/*',
        'routes/**/*',
        'package.json',
        'pnpm-lock.yaml',
        'package-lock.json',
        'composer.json',
        'composer.lock',
        'pubspec.yaml',
        'pubspec.lock',
        '.env',
        '.env.local',
        '.env.example',
      ],
      {
        cwd: projectRoot,
        onlyFiles: true,
        absolute: true,
        dot: true,
      },
    )
  ).sort();
  const hash = createHash('sha256');
  hash.update(workflow);

  for (const file of files) {
    hash.update(relative(projectRoot, file));
    hash.update(await readFile(file));
  }

  hash.update(JSON.stringify(extra));

  return hash.digest('hex');
}

export async function discoverTargetUrl(
  projectRoot: string,
  stack: string,
  explicit?: string,
): Promise<string | null> {
  if (explicit) {
    return explicit;
  }

  const envFiles = ['.env', '.env.local', '.env.example'];
  for (const envFile of envFiles) {
    const path = join(projectRoot, envFile);
    if (!existsSync(path)) {
      continue;
    }

    const content = await readFile(path, 'utf8');
    const match =
      content.match(/(?:^|\n)\s*APP_URL\s*=\s*["']?([^\n"']+)["']?/i) ??
      content.match(/(?:^|\n)\s*VITE_APP_URL\s*=\s*["']?([^\n"']+)["']?/i) ??
      content.match(/(?:^|\n)\s*VITE_BASE_URL\s*=\s*["']?([^\n"']+)["']?/i);
    if (match?.[1]) {
      return match[1].trim();
    }
  }

  if (stack === 'laravel') {
    return 'http://127.0.0.1:8000';
  }

  if (stack === 'react' || stack === 'vue') {
    return 'http://127.0.0.1:3000';
  }

  if (stack === 'flutter') {
    return 'http://127.0.0.1:8080';
  }

  return null;
}

export async function runProjectScript(
  projectRoot: string,
  scriptName: string,
  env: Record<string, string>,
  logDir: string,
): Promise<ProjectScriptResult> {
  const scriptPath = join(projectRoot, PATHS.SCRIPTS_DIR, scriptName);
  const stdoutPath = join(logDir, `${scriptName}.stdout.log`);
  const stderrPath = join(logDir, `${scriptName}.stderr.log`);
  await mkdir(logDir, { recursive: true });

  if (!existsSync(scriptPath)) {
    await writeFile(stdoutPath, '');
    await writeFile(
      stderrPath,
      `Missing script: ${toPosixPath(relative(projectRoot, scriptPath))}\n`,
    );
    return {
      script: scriptName,
      status: 'blocked',
      artifact_paths: [
        toPosixPath(relative(projectRoot, stdoutPath)),
        toPosixPath(relative(projectRoot, stderrPath)),
      ],
      exit_code: null,
      summary: `Missing project script ${scriptName}`,
    };
  }

  const result = await execa(scriptPath, {
    cwd: projectRoot,
    env,
    reject: false,
  });
  await writeFile(stdoutPath, result.stdout);
  await writeFile(stderrPath, result.stderr);

  return {
    script: scriptName,
    status: result.exitCode === 0 ? 'completed' : 'failed',
    artifact_paths: [
      toPosixPath(relative(projectRoot, stdoutPath)),
      toPosixPath(relative(projectRoot, stderrPath)),
    ],
    exit_code: result.exitCode ?? null,
    summary:
      result.exitCode === 0
        ? `${scriptName} completed`
        : `${scriptName} failed with exit code ${result.exitCode}`,
  };
}

export async function loadModuleDocs(
  projectRoot: string,
  focusModules: string[] = [],
): Promise<ModuleDocEvidence[]> {
  const moduleRoot = join(projectRoot, PATHS.MODULES_DIR);
  if (!existsSync(moduleRoot)) {
    return [];
  }

  const dirs = (await readdir(moduleRoot, { withFileTypes: true }))
    .filter((entry) => entry.isDirectory())
    .map((entry) => entry.name)
    .filter((moduleName) => focusModules.length === 0 || focusModules.includes(moduleName))
    .sort();

  return Promise.all(
    dirs.map(async (moduleName) => {
      const paths = (
        await fg(
          [
            `${moduleName}/business.md`,
            `${moduleName}/technical.md`,
            `${moduleName}/features/*/business.md`,
            `${moduleName}/features/*/technical.md`,
            `${moduleName}/api/endpoints.md`,
            `${moduleName}/api/schemas.md`,
            `${moduleName}/integration/contracts.md`,
            `${moduleName}/error-catalog.md`,
            `${moduleName}/ui/states.md`,
          ],
          {
            cwd: moduleRoot,
            onlyFiles: true,
            absolute: true,
          },
        )
      ).sort();

      const content = (
        await Promise.all(paths.map((path) => readFile(path, 'utf8').catch(() => '')))
      ).join('\n');

      return {
        module: moduleName,
        paths: paths.map((path) => toPosixPath(relative(projectRoot, path))),
        content,
      };
    }),
  );
}

export async function loadTests(
  projectRoot: string,
  focusModules: string[] = [],
): Promise<TestEvidence[]> {
  const files = (
    await fg(['tests/**/*.{ts,tsx,js,jsx,php,dart}', 'src/**/*.{test,spec}.{ts,tsx,js,jsx}'], {
      cwd: projectRoot,
      onlyFiles: true,
      absolute: true,
    })
  ).sort();

  const loweredFocus = focusModules.map((moduleName) => moduleName.toLowerCase());
  const filtered = focusModules.length
    ? files.filter((file) =>
        loweredFocus.some((moduleName) => file.toLowerCase().includes(moduleName)),
      )
    : files;

  return Promise.all(
    filtered.map(async (path) => ({
      path: toPosixPath(relative(projectRoot, path)),
      content: await readFile(path, 'utf8').catch(() => ''),
    })),
  );
}

export function parseSuspiciousRoutes(
  runtimeChecks: Array<{ path: string; status: number }>,
): SuspiciousRoute[] {
  return runtimeChecks
    .filter((entry) => entry.status > 0 && entry.status < 500)
    .map((entry) => ({
      path: entry.path,
      status: entry.status,
      kind:
        entry.path.includes('telescope') ||
        entry.path.includes('debug') ||
        entry.path.includes('clockwork')
          ? 'debug-surface'
          : entry.path.includes('.env')
            ? 'sensitive-file'
            : 'public-admin-surface',
    }));
}

export function summarizeCommandAvailability(scriptResults: ProjectScriptResult[]): string[] {
  return scriptResults
    .filter((result) => result.status !== 'completed')
    .map((result) => result.summary);
}

export function inferSourceArtifacts(
  baseDir: string,
  scriptResults: ProjectScriptResult[],
): string[] {
  const artifacts: string[] = [];

  for (const result of scriptResults) {
    artifacts.push(...result.artifact_paths);
  }

  return [
    ...new Set(
      artifacts.map((artifact) =>
        artifact.startsWith('.')
          ? artifact
          : toPosixPath(relative(dirname(baseDir), join(dirname(baseDir), artifact))),
      ),
    ),
  ];
}

// Skills shared across capabilities (cross-cutting finding handling) live under
// runtime/base/skills; capability-specific pentest skills live under the
// security capability. finding-normalizer was promoted to base (issue #89) so
// its RS-/DT-/MD-/PEN- vocabulary is not trapped under one capability.
const BASE_SKILLS = new Set(['finding-normalizer']);

export function skillPath(name: string): string {
  const root = BASE_SKILLS.has(name)
    ? 'runtime/base/skills'
    : 'runtime/capabilities/security/skills';
  return `${root}/${name}/SKILL.md`;
}

export function toolReferencePath(stack: string): string {
  return `docs/instructions/tools/${stack}/security-review-checklist.md`;
}

export function toReportId(prefix: 'PENTEST' | 'RETEST', date: Date): string {
  return `${prefix}-${toLocalTimestamp(date)}`;
}

export function extractRelevantTestPaths(
  tests: TestEvidence[],
  moduleName: string,
  keywords: string[],
): string[] {
  const loweredModule = moduleName.toLowerCase();
  return tests
    .filter((test) => {
      const haystack = `${test.path}\n${test.content}`.toLowerCase();
      return (
        haystack.includes(loweredModule) && keywords.some((keyword) => haystack.includes(keyword))
      );
    })
    .map((test) => test.path)
    .slice(0, 5);
}

export function parseSecretMatches(content: string): string[] {
  return content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .slice(0, 20);
}

export function safeBaseName(path: string): string {
  return basename(path).replace(/[^a-zA-Z0-9._-]+/g, '-');
}
